import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useT } from '@transifex/react'
import { useLocation } from 'react-router-dom'
import { Menu, ConfigProvider } from 'src/antd'

import {
  useContractorPersona,
  useExternalBillingEnabled,
  useHaulerB2bEnabled,
  useHaulerModuleEnabled,
  useInventoryEnabled,
  useLegacySensorAlertsEnabled,
  useMapPlanningEnabled,
  useMe,
  useRouteStudioEnabled,
} from 'src/hooks'
import { useRoutingHistory } from 'src/reducers/routing/child-router-factory'
import {
  HISTORY_STORE_ACTIONS_KEY,
  RESET_HISTORY,
} from 'src/reducers/routing/store'
import { rawTheme } from 'src/styles/raw'
import { ResolvedTheme, useResolvedTheme } from 'src/theme/useThemeMode'

import { findBestMatchKey } from './findBestMatchKey'
import type {
  AntdMenuItem,
  MenuItemWithLink,
  MenuItemWithSkipLinks,
} from './findBestMatchKey'
import getItems from './getItems'
import { SidebarContainer, SidebarMenuWrapper } from './styles'

type NavigationMenuProps = {
  openedKey?: string
  collapsed?: boolean
}

// SubMenuStyle lives in ./styles (styles-only module) so GlobalStyles can use
// it without pulling this whole menu graph into the login entry chunk;
// re-exported here for existing importers. SidebarContainer/SidebarMenuWrapper
// live there too (PRO-543) so the sticky-sider test can import them light.
export { SubMenuStyle } from './styles'

// Layout tokens are theme-independent.
const MENU_STRUCTURAL = {
  itemBorderRadius: 6,
  subMenuItemBorderRadius: 6,
  itemPaddingInline: 0,
  iconSize: 18,
  collapsedIconSize: 20,
  borderInlineEnd: 0,
  itemHeight: 40,
  collapsedWidth: 64,
}

// PRODUCT-154: antd component tokens must be REAL hex, never `var(--wh-*)`
// (cssinjs may run color math on them). Keyed per theme so light stays
// byte-identical to the pre-conversion config; dark is a readable neutral first
// pass — the brand-tinted (blue) dark selection is a polish-pass follow-up.
const getMenuThemeConfig = (resolved: ResolvedTheme) => {
  const c = rawTheme(resolved)
  const colors =
    resolved === 'dark'
      ? {
          itemColor: c.gray900,
          itemHoverColor: c.gray900,
          itemSelectedColor: c.gray900,
          itemSelectedBg: c.gray400,
          itemActiveBg: c.gray300,
          itemHoverBg: c.gray300,
          popupBg: c.gray200,
        }
      : {
          itemColor: c.navy900,
          itemHoverColor: c.navy900,
          itemSelectedColor: c.navy900,
          itemSelectedBg: c.blue200,
          itemActiveBg: c.blue100,
          itemHoverBg: c.blue100,
          popupBg: c.gray200,
        }
  return {
    hashed: false,
    // Menu container bg tracks the sider surface (bgSider), so the sider and its
    // menu read as ONE surface — no dark-mode gap below the items (PRODUCT-154
    // phase 4c). LIGHT: #FFFFFF, byte-identical to antd's default container bg.
    token: { colorBgContainer: c.bgSider },
    components: { Menu: { ...colors, ...MENU_STRUCTURAL } },
  }
}

const NavigationMenu: React.FC<NavigationMenuProps> = ({
  openedKey,
  collapsed,
}) => {
  const t = useT()
  const me = useMe()
  const resolvedTheme = useResolvedTheme()
  const menuThemeConfig = useMemo(
    () => getMenuThemeConfig(resolvedTheme),
    [resolvedTheme]
  )
  // Foreman gating (3.12.6): a contractor foreman holds only day-ops
  // permissions, so manager-only workspace destinations are hidden from them.
  // canManageCrew (PRO-30) additionally gates the workspace Users entry on
  // the exact driver__* strings the BE's user-management lane checks.
  const { canManageContractor, canManageCrew } = useContractorPersona()
  const [history, { roles }] = useRoutingHistory()
  const { pathname } = useLocation()
  const [openKeys, setOpenKeys] = useState<string[]>([])

  const invoicingEnabled = Boolean(
    me?.company?.companySettings?.invoicingEnabled
  )
  // "Billing handled externally" (Visma & co): the company keeps the invoicing
  // backend for its integration but the internal module is hidden — same
  // single-source hook the /app/invoicing route gate reads.
  const externalBillingEnabled = useExternalBillingEnabled()
  // D01/D33: staged-rollout flag; the hook is the single source used
  // by both this menu and the module's route gate (SBX-341).
  const routeStudioEnabled = useRouteStudioEnabled()
  const insightsEnabled = Boolean(me?.company?.companySettings?.insightsEnabled)
  // Per-company Hauler-module opt-in: with it off the menu must look exactly
  // as it did before the hauler build (fail-safe OFF when the field is absent).
  const haulerModuleEnabled = useHaulerModuleEnabled()
  // Per-company Hauler Mode (Erhverv) opt-in — same single-source hook the
  // module's route gate reads, so discovery and access always agree.
  const haulerB2bEnabled = useHaulerB2bEnabled()
  // Per-company Inventory (stock) module opt-in (PRO-483) — same
  // single-source hook the module's route gate reads.
  const inventoryEnabled = useInventoryEnabled()
  // Per-company Map Planning workspace opt-in (COMPASS-1190) — same
  // single-source hook the /app/map-planning route gate reads.
  const mapPlanningEnabled = useMapPlanningEnabled()
  // Per-company legacy fill-level sensor Alerts opt-in (PRO-560) — same
  // single-source hook the notifications route gate reads, so the nav entry
  // and the route agree. Fail-open (see the hook), so the entry stays until a
  // company is explicitly opted out.
  const legacySensorAlertsEnabled = useLegacySensorAlertsEnabled()

  const handleLinkClick = useCallback(
    (path: string, state?: Record<string, unknown>) => {
      history.push(path, {
        ...state,
        [HISTORY_STORE_ACTIONS_KEY]: [RESET_HISTORY],
      })
    },
    [history]
  )

  const menuItems = useMemo(
    () =>
      getItems(t, roles, handleLinkClick, invoicingEnabled, {
        canManageContractor,
        canManageCrew,
        haulerModuleEnabled,
        haulerB2bEnabled,
        insightsEnabled,
        routeStudioEnabled,
        inventoryEnabled,
        mapPlanningEnabled,
        legacySensorAlertsEnabled,
        externalBillingEnabled,
      }),
    [
      t,
      roles,
      handleLinkClick,
      invoicingEnabled,
      externalBillingEnabled,
      canManageContractor,
      canManageCrew,
      haulerModuleEnabled,
      haulerB2bEnabled,
      routeStudioEnabled,
      insightsEnabled,
      inventoryEnabled,
      mapPlanningEnabled,
      legacySensorAlertsEnabled,
    ]
  )
  const validMenuItems = useMemo(
    () => menuItems.filter(Boolean) as AntdMenuItem[],
    [menuItems]
  )

  // link/skipLinks exist for findBestMatchKey only — antd spreads unknown
  // item props onto the rendered <li>, so strip them from what the Menu
  // itself receives to keep React's unknown-prop warning out of the console.
  const renderableMenuItems = useMemo(() => {
    const strip = (items: AntdMenuItem[]): AntdMenuItem[] =>
      items.map((item) => {
        if (!item || typeof item !== 'object') return item
        const {
          link: _link,
          skipLinks: _skipLinks,
          children,
          ...rest
        } = item as AntdMenuItem & MenuItemWithSkipLinks & MenuItemWithLink
        return {
          ...rest,
          ...(Array.isArray(children)
            ? { children: strip(children as AntdMenuItem[]) }
            : {}),
        } as AntdMenuItem
      })
    return strip(validMenuItems)
  }, [validMenuItems])

  const selectedKeys = useMemo(() => {
    if (openedKey) return [openedKey]
    const bestMatch = findBestMatchKey(pathname, validMenuItems)
    return bestMatch ? [bestMatch] : []
  }, [openedKey, pathname, validMenuItems])

  /**
   * PRO-746: the sync below acts on a CHANGE of selection, not on every run.
   *
   * Its dependencies are rebuilt on every render of this component, so it used
   * to re-add the parent key immediately after `onOpenChange` had removed it:
   * clicking an already-open group collapsed it for exactly ONE FRAME and it
   * sprang back. Measured in Chrome on the Intelligence group with Monitor
   * selected — expanded at 1ms, collapsed at 13ms, expanded again at 25ms.
   * That flash is the "glitch" the captain reported, and the comment below had
   * always promised the opposite ("the user can still fold any group by
   * hand"). This ref is what makes that sentence true.
   */
  const autoOpenedForSelection = useRef<string | null>(null)

  // PRO-218: the group that owns the current page stays open. openKeys
  // is controlled state that starts empty and is lost when the menu
  // remounts on navigation (RESET_HISTORY), so without this sync the
  // selected child sat hidden behind a collapsed group — the contractor
  // Workspace lived permanently shut and the menu "seemed broken". The
  // user can still fold any group by hand; the next navigation into one
  // of its children reopens it.
  useEffect(() => {
    // PRO-220: collapsed rail = popup mode — antd drives the hover
    // popups from its own internal state there (openKeys is handed
    // back to it below), so the sync only runs for the expanded rail,
    // and re-runs on expand in case navigation happened while
    // collapsed. Deliberately NOT recorded while collapsed, so expanding
    // the rail still opens the group that owns the page.
    if (collapsed) return
    const [selected] = selectedKeys
    if (!selected) return
    // Already opened for this selection — a later render must not undo a
    // manual fold.
    if (autoOpenedForSelection.current === selected) return
    autoOpenedForSelection.current = selected
    const parent = validMenuItems.find(
      (item) =>
        item &&
        typeof item === 'object' &&
        'children' in item &&
        Array.isArray(item.children) &&
        item.children.some(
          (child) =>
            child &&
            typeof child === 'object' &&
            'key' in child &&
            String(child.key) === selected
        )
    )
    if (!parent || !('key' in parent) || parent.key == null) return
    const parentKey = String(parent.key)
    setOpenKeys((was) => (was.includes(parentKey) ? was : [...was, parentKey]))
  }, [selectedKeys, validMenuItems, collapsed])

  const handleOpenChange = useCallback(
    (keys: string[]) => {
      // PRO-220: hover-driven popup changes in the collapsed rail must
      // not leak into the expanded rail's open-group state.
      if (!collapsed) setOpenKeys(keys)
    },
    [collapsed]
  )

  return (
    <SidebarContainer>
      <SidebarMenuWrapper>
        <ConfigProvider theme={menuThemeConfig}>
          <Menu
            mode="inline"
            sidebar
            items={renderableMenuItems}
            selectedKeys={selectedKeys}
            // PRO-220: controlled only while expanded; the collapsed
            // rail's hover popups are antd's own (a pinned key would
            // hold a popup open beside the rail, and forcing [] kills
            // the hover popups instead).
            openKeys={collapsed ? undefined : openKeys}
            onOpenChange={handleOpenChange}
          />
        </ConfigProvider>
      </SidebarMenuWrapper>
    </SidebarContainer>
  )
}

export default NavigationMenu
